import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@astrum/ui";
import { api, ApiError } from "../../lib/api.js";
import type { Message } from "../../lib/api.js";
import { useAuth } from "../../lib/auth.js";
import { newClientMessageId } from "../../lib/chat-format.js";
import { useRealtimeFrames } from "../../lib/realtime-context.js";

type ThreadPage = { root: Message; replies: Message[]; hasMore: boolean; following: boolean };

const THREAD_PAGE = 50;

export interface ThreadState {
  root: Message | null;
  replies: Message[];
  loading: boolean;
  /** The thread could not be opened (gone, or no longer readable). */
  failed: boolean;
  hasMoreOlder: boolean;
  loadingOlder: boolean;
  following: boolean;
  sending: boolean;
  loadOlder: () => void;
  send: (body: string, alsoInChannel: boolean) => Promise<boolean>;
  setFollowing: (following: boolean) => void;
}

/**
 * One open thread: its root, its replies, and the viewer's follow state.
 *
 * Replies arrive over the same realtime gateway as channel messages — a
 * `message` frame whose parent is this root — so the panel stays live without
 * a poll. Opening the thread marks it read on the server, and so does each
 * reply that lands while it is open.
 */
export function useThread(channelId: string | undefined, rootId: string | undefined): ThreadState {
  const { viewer } = useAuth();
  const { toast } = useToast();
  const [root, setRoot] = useState<Message | null>(null);
  const [replies, setReplies] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [following, setFollowingState] = useState(false);
  const [sending, setSending] = useState(false);

  // The open thread, for responses and frames to check they still belong to it.
  const openRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    openRef.current = channelId && rootId ? `${channelId}:${rootId}` : undefined;
  }, [channelId, rootId]);

  const markRead = useCallback((forChannelId: string, forRootId: string) => {
    api.post(`/channels/${forChannelId}/messages/${forRootId}/thread/read`).catch(() => undefined);
  }, []);

  useEffect(() => {
    setRoot(null);
    setReplies([]);
    setFailed(false);
    setHasMoreOlder(false);
    if (!channelId || !rootId) return;

    let cancelled = false;
    setLoading(true);
    api
      .get<ThreadPage>(`/channels/${channelId}/messages/${rootId}/thread?limit=${THREAD_PAGE}`)
      .then((res) => {
        if (cancelled) return;
        setRoot(res.root);
        setReplies(res.replies);
        setHasMoreOlder(res.hasMore);
        setFollowingState(res.following);
        markRead(channelId, rootId);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [channelId, rootId, markRead]);

  const loadOlder = useCallback(() => {
    if (!channelId || !rootId || loadingOlder || !hasMoreOlder) return;
    const oldest = replies[0];
    if (!oldest) return;
    setLoadingOlder(true);
    api
      .get<ThreadPage>(`/channels/${channelId}/messages/${rootId}/thread?before=${oldest.id}&limit=${THREAD_PAGE}`)
      .then((res) => {
        if (openRef.current !== `${channelId}:${rootId}`) return;
        setReplies((prev) => [...res.replies, ...prev]);
        setHasMoreOlder(res.hasMore);
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not load earlier replies.", { tone: "danger" });
      })
      .finally(() => setLoadingOlder(false));
  }, [channelId, rootId, loadingOlder, hasMoreOlder, replies, toast]);

  /** Adds a reply, or replaces the one already held (the echo of our own send). */
  const mergeReply = useCallback((incoming: Message) => {
    setReplies((prev) => {
      if (prev.some((m) => m.id === incoming.id)) return prev.map((m) => (m.id === incoming.id ? incoming : m));
      return [...prev, incoming].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    });
  }, []);

  useRealtimeFrames((frame) => {
    if (!channelId || !rootId) return;
    if (frame.t === "message") {
      const m = frame.message;
      if (m.channelId !== channelId || m.parentMessageId !== rootId || !m.inThread) return;
      mergeReply(m);
      if (m.authorId !== viewer?.id) markRead(channelId, rootId);
    } else if (frame.t === "link_preview" && frame.channelId === channelId) {
      setReplies((prev) => prev.map((m) => (m.id === frame.messageId ? { ...m, linkPreview: frame.linkPreview } : m)));
    } else if (frame.t === "thread" && frame.channelId === channelId && frame.rootMessageId === rootId) {
      setRoot((prev) => (prev ? { ...prev, thread: frame.thread.replyCount > 0 ? frame.thread : null } : prev));
      // A reply was withdrawn: drop any that are no longer counted.
      if (frame.thread.replyCount === 0) setReplies([]);
    }
  });

  const send = useCallback(
    async (body: string, alsoInChannel: boolean) => {
      if (!channelId || !rootId) return false;
      setSending(true);
      try {
        const res = await api.post<{ message: Message }>(`/channels/${channelId}/messages`, {
          body,
          clientMessageId: newClientMessageId(),
          parentMessageId: rootId,
          inThread: true,
          ...(alsoInChannel ? { alsoInChannel: true } : {}),
        });
        if (openRef.current === `${channelId}:${rootId}`) {
          mergeReply(res.message);
          // Posting follows the thread, as the server has just done.
          setFollowingState(true);
        }
        return true;
      } catch (error: unknown) {
        toast(error instanceof ApiError ? error.message : "Could not send that reply.", { tone: "danger" });
        return false;
      } finally {
        setSending(false);
      }
    },
    [channelId, rootId, mergeReply, toast],
  );

  const setFollowing = useCallback(
    (next: boolean) => {
      if (!channelId || !rootId) return;
      setFollowingState(next);
      const path = `/channels/${channelId}/messages/${rootId}/thread/follow`;
      (next ? api.put(path) : api.delete(path)).catch((error: unknown) => {
        setFollowingState(!next);
        toast(error instanceof ApiError ? error.message : "Could not update that thread.", { tone: "danger" });
      });
    },
    [channelId, rootId, toast],
  );

  return { root, replies, loading, failed, hasMoreOlder, loadingOlder, following, sending, loadOlder, send, setFollowing };
}
